import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import {
  SimplyWebUser,
  SaveSimplyWebUserRequest,
  useCreateSimplyWebUserMutation,
  useDisableSimplyWebUserMutation,
  useGetSimplyWebUsersQuery,
  useResetSimplyWebUserPasswordMutation,
  useUpdateSimplyWebUserMutation,
} from "../../../api/admin/users/SimplyWebUsersApi";

type UserFormValues = {
  username: string;
  email: string;
  name?: string;
  birthdate?: Dayjs;
  enabled?: boolean;
  temporaryPassword?: string;
  sendInvite?: boolean;
};

function formatDate(value?: string) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

function getErrorMessage(error: unknown) {
  if (!error || typeof error !== "object") {
    return "Request failed";
  }
  const maybeError = error as { data?: { message?: string }; error?: string };
  return maybeError.data?.message || maybeError.error || "Request failed";
}

export default function SimplyWebUserManagement() {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [editingUser, setEditingUser] = useState<SimplyWebUser | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userForm] = Form.useForm<UserFormValues>();

  const {
    data: usersData,
    error: usersError,
    isFetching: isFetchingUsers,
  } = useGetSimplyWebUsersQuery(appliedSearch ? { search: appliedSearch } : undefined);

  const [createUser, { isLoading: isCreatingUser }] = useCreateSimplyWebUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateSimplyWebUserMutation();
  const [disableUser, { isLoading: isDisablingUser }] = useDisableSimplyWebUserMutation();
  const [resetPassword, { isLoading: isResettingPassword }] = useResetSimplyWebUserPasswordMutation();

  useEffect(() => {
    if (!isUserModalOpen) {
      return;
    }
    userForm.setFieldsValue({
      username: editingUser?.username || "",
      email: editingUser?.email || "",
      name: editingUser?.name || "",
      birthdate: editingUser?.birthdate ? dayjs(editingUser.birthdate) : undefined,
      enabled: editingUser?.enabled ?? true,
      sendInvite: true,
      temporaryPassword: "",
    });
  }, [editingUser, isUserModalOpen, userForm]);

  const users = usersData?.users || [];

  const columns: ColumnsType<SimplyWebUser> = [
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{record.email || record.username}</Typography.Text>
          <Typography.Text type="secondary">{record.username}</Typography.Text>
        </Space>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (value) => value || "-",
    },
    {
      title: "Birthdate",
      dataIndex: "birthdate",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Space>
          <Tag color={record.enabled ? "green" : "red"}>{record.enabled ? "Enabled" : "Disabled"}</Tag>
          {record.status && <Tag>{record.status}</Tag>}
          {record.emailVerified && <Tag color="blue">Email Verified</Tag>}
        </Space>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: formatDate,
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => openEditUser(record)}>
            Edit
          </Button>
          <Button size="small" loading={isResettingPassword} onClick={() => handleResetPassword(record)}>
            Reset Password
          </Button>
          <Popconfirm
            title="Disable this user?"
            okText="Disable"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDisableUser(record)}
            disabled={!record.enabled}
          >
            <Button size="small" danger disabled={!record.enabled} loading={isDisablingUser}>
              Disable
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  function openCreateUser() {
    setEditingUser(null);
    setIsUserModalOpen(true);
  }

  function openEditUser(user: SimplyWebUser) {
    setEditingUser(user);
    setIsUserModalOpen(true);
  }

  async function handleSaveUser(values: UserFormValues) {
    try {
      const body: SaveSimplyWebUserRequest = {
        username: values.username,
        email: values.email,
        name: values.name,
        birthdate: values.birthdate ? values.birthdate.format("YYYY-MM-DD") : undefined,
        enabled: values.enabled,
        temporaryPassword: values.temporaryPassword || undefined,
        suppressInvite: values.sendInvite === false,
      };
      if (editingUser) {
        await updateUser(body).unwrap();
        message.success("User updated");
      } else {
        await createUser(body).unwrap();
        message.success("User created");
      }
      setIsUserModalOpen(false);
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  }

  async function handleDisableUser(user: SimplyWebUser) {
    try {
      await disableUser(user.username).unwrap();
      message.success("User disabled");
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  }

  async function handleResetPassword(user: SimplyWebUser) {
    try {
      await resetPassword(user.username).unwrap();
      message.success("Password reset email sent");
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  }

  return (
    <>
      <Typography.Title level={2}>Website Users</Typography.Title>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {usersError && <Alert type="error" message={getErrorMessage(usersError)} />}
        <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
          <Input.Search
            placeholder="Search by email or name prefix"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(value) => setAppliedSearch(value.trim())}
            style={{ maxWidth: 360 }}
          />
          <Button type="primary" onClick={openCreateUser}>
            New User
          </Button>
        </Space>
        <Table
          rowKey="username"
          columns={columns}
          dataSource={users}
          loading={isFetchingUsers}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Space>
      <Modal
        title={editingUser ? "Edit User" : "New User"}
        open={isUserModalOpen}
        onCancel={() => setIsUserModalOpen(false)}
        onOk={() => userForm.submit()}
        okText={editingUser ? "Save" : "Create"}
        confirmLoading={isCreatingUser || isUpdatingUser}
        destroyOnClose
      >
        <Form form={userForm} layout="vertical" onFinish={handleSaveUser}>
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: "Username is required" }]}
            extra={editingUser ? undefined : "Typically the user's email address"}
          >
            <Input disabled={Boolean(editingUser)} autoComplete="username" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item label="Name" name="name">
            <Input autoComplete="name" />
          </Form.Item>
          <Form.Item label="Birthdate" name="birthdate">
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          {!editingUser && (
            <>
              <Form.Item label="Temporary Password" name="temporaryPassword">
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item label="Send Invite" name="sendInvite" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
